// CourtAvailabilityView.js

import React, { useEffect, useState, useRef } from 'react';
import { Typography, Input, CircularProgress, Button, Collapse, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import CourtAvailabilityTable from './CourtAvailabilityTable';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { logEvent } from "firebase/analytics";
import { useNavigate } from 'react-router-dom';
import { getFavoriteUpdateStatus } from '../utils/favoriteAccess';
import { firebaseConfig } from '../firebase/config';
import { addCourtDistances } from '../utils/courtDistance';
import CourtCardsSkeleton from './CourtCardsSkeleton';

function accentAndCaseRemover(initialString) {
    if (initialString) {
        const replacementDict = {
            'a': ['â', 'à', 'ä', 'Â', 'À', 'Ä'],
            'e': ['é', 'ê', 'ë', 'è', 'É', 'È', 'Ê', 'Ë'],
            'c': ['ç', 'Ç'],
            'i': ['î', 'ï', 'ì', 'Î', 'Ï', 'Ì'],
            'o': ['ô', 'ö', 'ò', 'Ô', 'Ö', 'Ò'],
            'u': ['û', 'ü', 'ù', 'Û', 'Ü', 'Ù']
        }
        for (var key in replacementDict) {
            replacementDict[key].forEach(
                specialLetter => {
                    initialString = initialString.replace(new RegExp(specialLetter, 'g'), key);
                }
            );
        }

        initialString = initialString.toLowerCase();
    }
    return initialString
}

const translations = {
    en: {
        favoriteLimitTitle: "Favorite Limit Reached",
        favoriteLimitMessage: "Free plan allows only 1 favorite court. Please remove the court from favorites or upgrade to Premium plan to add more favorites.",
        upgrade: "Upgrade",
        close: "Close"
    },
    fr: {
        favoriteLimitTitle: "Limite de favoris atteinte",
        favoriteLimitMessage: "Le plan gratuit permet d'avoir un seul terrain favori. Veuillez retirer le terrain de vos favoris ou mettre à niveau au plan Premium pour en ajouter plus.",
        upgrade: "Mettre à niveau",
        close: "Fermer"
    }
};

// Popular parks to show by default when no search is entered and geolocation is not available
const POPULAR_PARKS_DEFAULT = [
    "1734", // Parc La Fontaine, terrains sportifs (Le Plateau-Mont-Royal) - Very popular, many courts
    "1726", // Parc Jeanne-Mance, terrains sportifs (Le Plateau-Mont-Royal) - Central location, popular
    "792", // Parc Nicolas-Viel
    "1755", // Parc Martin-Luther-King, terrain de sport (Côte-des-Neiges - Notre-Dame-de-Grâce) - Central
    "1070", // Parc Arthur-Therrien, terrain de sport (Verdun) - Good availability
    "1068", // Parc Elgar, terrain de sport (Verdun) - Popular Verdun location
    "70",   // Parc Giuseppe-Garibaldi, terrain de sport (Saint-Léonard) - Popular east-end location
    "1884", // Parc Père-Marquette, terrain de sport (Côte-des-Neiges - Notre-Dame-de-Grâce) - Central location
    "2060", // Terrains sportifs du parc LaSalle (Lachine) - Popular west-end location
    "1033"  // Terrain de sport - Parc Gohier (Saint-Laurent) - Good north-end option
];

const CourtAvailabilityView = ({
    searchField,
    onSearchFieldChange,
    isFrench,
    analytics,
    setSelectedTab,
    updateLastRefreshTime,
    autoRefreshEnabled,
    accountType: effectiveAccountType
}) => {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState(searchField || '');
    const [isMobile, setIsMobile] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
    const [hasResolvedInitialResults, setHasResolvedInitialResults] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [isOffline, setIsOffline] = useState(
        typeof navigator !== 'undefined' && navigator.onLine === false
    );
    const [favoritesLoaded, setFavoritesLoaded] = useState(false);
    const [geoPermission, setGeoPermission] = useState('unknown');
    const [geoLoading, setGeoLoading] = useState(false); // Track background geolocation
    const [favorites, setFavorites] = useState([]);
    const [dataToShow, setDataToShow] = useState([]);
    const [showNoAvailability, setShowNoAvailability] = useState(false);
    const [expandedCourts, setExpandedCourts] = useState(new Set());
    const [refreshInterval, setRefreshInterval] = useState(5 * 60 * 1000); // Default 5 minutes
    const scrollPositionRef = useRef(0);
    const isRefreshing = useRef(false);
    const refreshTimeoutRef = useRef(null);
    const [showFavoriteLimitDialog, setShowFavoriteLimitDialog] = useState(false);
    const navigate = useNavigate();

    const requestCourtDistances = () => {
        if (!("geolocation" in navigator)) {
            setGeoPermission('unavailable');
            return;
        }

        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const userLocation = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                };

                setGeoPermission('granted');
                setData((currentCourts) => addCourtDistances(currentCourts, userLocation));
                setGeoLoading(false);
            },
            (error) => {
                console.error("Error getting current location:", error);
                setGeoLoading(false);
                setGeoPermission(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
            },
            {
                timeout: 10000,
                enableHighAccuracy: false,
                maximumAge: 300000
            }
        );
    };

    // Function to save scroll position
    const saveScrollPosition = () => {
        if (!isRefreshing.current) {
            scrollPositionRef.current = Math.max(0, Math.floor(window.scrollY));
        }
    };

    // Function to restore scroll position
    const restoreScrollPosition = () => {
        const scrollTarget = Math.max(0, Math.floor(Number(scrollPositionRef.current)));

        // Use multiple attempts with increasing delays
        const attemptScroll = (attempt = 1) => {
            if (attempt > 3) {
                isRefreshing.current = false;
                return;
            }

            setTimeout(() => {
                window.scrollTo(0, scrollTarget);

                // Verify if scroll worked
                if (Math.abs(window.scrollY - scrollTarget) > 10) {
                    attemptScroll(attempt + 1);
                } else {
                    isRefreshing.current = false;
                }
            }, attempt * 100);
        };

        requestAnimationFrame(() => attemptScroll());
    };

    // Add state to track selected dates for each court
    const [selectedDates, setSelectedDates] = useState({});

    // Modify the handleTabChange in CourtAvailabilityTable to update the parent's state
    const handleTabChange = (courtId, newDate) => {
        setSelectedDates(prev => ({
            ...prev,
            [courtId]: newDate
        }));
    };

    const FavoriteLimitDialog = () => (
        <Dialog
            open={showFavoriteLimitDialog}
            onClose={() => setShowFavoriteLimitDialog(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {isFrench ? translations.fr.favoriteLimitTitle : translations.en.favoriteLimitTitle}
            </DialogTitle>
            <DialogContent>
                <Typography>
                    {isFrench ? translations.fr.favoriteLimitMessage : translations.en.favoriteLimitMessage}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowFavoriteLimitDialog(false)}>
                    {isFrench ? translations.fr.close : translations.en.close}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        navigate('/profile');
                        setShowFavoriteLimitDialog(false);
                    }}
                >
                    {isFrench ? translations.fr.upgrade : translations.en.upgrade}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const updateFavorites = (newFavorites) => {
        const user = firebase.auth().currentUser;
        if (!user) return favorites;

        const updateStatus = getFavoriteUpdateStatus(effectiveAccountType, newFavorites);
        if (updateStatus === 'account-loading') {
            return favorites;
        }

        // Enforce the limit only for an explicit user action by a confirmed
        // free account. Existing stored favorites are never truncated.
        if (updateStatus === 'limit-reached') {
            // Show the limit dialog
            setShowFavoriteLimitDialog(true);
            // Log the attempt
            logEvent(analytics, 'favorite_limit_reached', {
                currentCount: favorites.length,
                attemptedCount: newFavorites.length
            });
            // Return current favorites to keep the star in its current state
            return favorites;
        }

        // Process the update if we're here
        processFavoriteUpdate(newFavorites);
        return newFavorites;
    };

    const processFavoriteUpdate = async (newFavorites) => {
        const user = firebase.auth().currentUser;
        if (!user) return;

        // Update database first
        try {
            await firebase.firestore().collection('userFavorites').doc(user.uid).set({
                favorites: newFavorites
            });

            let updatedDataToShow = [...dataToShow];

            // If a court is added to favorites, remove it from dataToShow
            for (let fav of newFavorites) {
                if (!favorites.includes(fav)) {
                    updatedDataToShow = updatedDataToShow.filter(item => item.id !== fav.toString());
                }
            }

            // If a court is removed from favorites, add it back to dataToShow
            for (let fav of favorites) {
                if (!newFavorites.includes(fav) && data.some(item => item.id === fav.toString())) {
                    const courtToAddBack = data.find(item => item.id === fav.toString());
                    updatedDataToShow.push(courtToAddBack);
                }
            }

            // Re-sort dataToShow based on distance
            updatedDataToShow.sort((a, b) => a.distanceInKm - b.distanceInKm);

            setDataToShow(updatedDataToShow);
            setFavorites(newFavorites);
        } catch (error) {
            console.error('Error updating favorites:', error);
        }
    };

    const handleResize = () => {
        const isMobile = window.innerWidth < 600;
        setIsMobile(isMobile);
    };

    const handleCourtExpansion = (courtId, isExpanded) => {
        setExpandedCourts(prev => {
            const newSet = new Set(prev);
            if (isExpanded) {
                newSet.add(courtId);
            } else {
                newSet.delete(courtId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            isRefreshing.current = true;
            console.log("Setting isRefreshing to true in fetchData");
            const currentScroll = window.scrollY;
            scrollPositionRef.current = currentScroll;

            setIsLoading(true);
            setLoadFailed(false);

            if (!firebase.apps.length) {
                firebase.initializeApp(firebaseConfig);
            }

            const firestore = firebase.firestore();

            try {
                // PHASE 1: Fetch all data immediately (COURTS + FAVORITES)
                // We don't wait for geolocation here
                const [overviewSnapshot, userDoc, newInterval] = await Promise.all([
                    firestore.collection('overview').doc('loadedSitesTable').get(),
                    firebase.auth().currentUser ? firestore.collection('userFavorites').doc(firebase.auth().currentUser.uid).get() : null,
                    updateLastRefreshTime() // This returns a promise too
                ]);

                const snapShotData = overviewSnapshot.data();

                if (!snapShotData) {
                    console.error("No data received from Firebase");
                    setLoadFailed(true);
                    setIsLoading(false);
                    isRefreshing.current = false;
                    return;
                }

                // Initial processing WITHOUT distances
                const initialData = Object.entries(snapShotData).map(([doc, courtData]) => ({
                    ...courtData,
                    id: doc,
                    distanceInKm: null // Initially null
                }));

                // Process favorites
                let currentFavorites = [];
                if (userDoc && userDoc.exists) {
                    currentFavorites = userDoc.data().favorites || [];
                }

                // UPDATE UI IMMEDIATELY
                setData(initialData);
                setHasLoadedInitialData(true);
                setFavorites(currentFavorites);
                setFavoritesLoaded(true);
                setRefreshInterval(newInterval);
                setIsLoading(false); // Stop loading spinner immediately

                // Restore scroll position
                setTimeout(restoreScrollPosition, 300);

                // PHASE 2: Background Geolocation
                if ("geolocation" in navigator) {
                    setGeoLoading(true); // Indication that we are calculating distances

                    // This is now non-blocking for the initial UI render
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const userLocation = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude
                            };

                            setGeoPermission('granted');

                            // Now update data WITH distances
                            const dataWithDistances = addCourtDistances(initialData, userLocation);

                            // Update state with distances
                            setData(dataWithDistances);
                            setGeoLoading(false);
                        },
                        (error) => {
                            console.error("Error getting current location:", error);
                            setGeoLoading(false);
                            if (error.code === error.PERMISSION_DENIED) {
                                setGeoPermission('denied');
                            } else if (error.code === error.TIMEOUT) {
                                console.error("Geolocation timeout");
                                setGeoPermission('unavailable');
                            } else {
                                setGeoPermission('unavailable');
                            }
                        },
                        {
                            timeout: 10000,
                            enableHighAccuracy: false,
                            maximumAge: 300000
                        }
                    );
                } else {
                    setGeoPermission('denied');
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setLoadFailed(true);
                setIsLoading(false);
                isRefreshing.current = false;
            }
        };

        fetchData();
        handleResize();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', saveScrollPosition);

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', saveScrollPosition);
        };
    }, [updateLastRefreshTime, loadAttempt]);

    useEffect(() => {
        const retryWhenOnline = () => {
            setIsOffline(false);
            setLoadAttempt(current => current + 1);
        };
        const showOfflineState = () => setIsOffline(true);
        window.addEventListener('online', retryWhenOnline);
        window.addEventListener('offline', showOfflineState);
        return () => {
            window.removeEventListener('online', retryWhenOnline);
            window.removeEventListener('offline', showOfflineState);
        };
    }, []);

    // Separate effect for handling auto-refresh scheduling
    useEffect(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        if (autoRefreshEnabled) {
            refreshTimeoutRef.current = setTimeout(() => {
                const fetchData = async () => {
                    isRefreshing.current = true;
                    const currentScroll = window.scrollY;
                    scrollPositionRef.current = currentScroll;

                    try {
                        const [overviewSnapshot, userDoc, newInterval] = await Promise.all([
                            firebase.firestore().collection('overview').doc('loadedSitesTable').get(),
                            firebase.auth().currentUser ? firebase.firestore().collection('userFavorites').doc(firebase.auth().currentUser.uid).get() : null,
                            updateLastRefreshTime()
                        ]);

                        const snapShotData = overviewSnapshot.data();
                        const updatedData = Object.entries(snapShotData).map(([doc, courtData]) => ({
                            ...courtData,
                            id: doc,
                            distanceInKm: data.find(item => item.id === doc)?.distanceInKm
                        }));

                        setData(updatedData);
                        setRefreshInterval(newInterval);
                        if (userDoc && userDoc.exists) {
                            setFavorites(userDoc.data().favorites || []);
                        }
                        setTimeout(restoreScrollPosition, 300);
                    } catch (error) {
                        console.error("Error fetching data:", error);
                    } finally {
                        // nothing
                    }
                };

                fetchData();
            }, refreshInterval);
        }

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [autoRefreshEnabled, refreshInterval, data]);

    useEffect(() => {
        const hasDistanceResults = data.some(item => item.distanceInKm !== null);

        if (searchQuery.length >= 3 || hasDistanceResults) {
            setIsLoading(true);
            const timer = setTimeout(() => {
                const filtered = data.filter((item) => {
                    if (searchQuery.length >= 3) {
                        let cleanSiteName = accentAndCaseRemover(item.siteName.trim().replace(/ +/g, "").toLowerCase());
                        let cleanSearchQuery = accentAndCaseRemover(searchQuery.trim().replace(/ +/g, "").toLowerCase());
                        return cleanSiteName.includes(cleanSearchQuery);
                    }
                    if (item.distanceInKm !== null) {
                        return true;
                    }
                    return false;
                });

                // First, split courts into available and unavailable
                const { available, unavailable } = getFilteredCourts(filtered);

                // Sort each group by distance
                const sortedAvailable = available.sort((a, b) => a.distanceInKm - b.distanceInKm);
                const sortedUnavailable = unavailable.sort((a, b) => a.distanceInKm - b.distanceInKm);

                // Combine them with available courts first, then take top 10
                const combinedSorted = [...sortedAvailable, ...sortedUnavailable].slice(0, 10);

                if (favoritesLoaded) {
                    setDataToShow(combinedSorted.filter(item => !favorites.includes(parseInt(item.id))));
                } else {
                    setDataToShow([]);
                }

                setIsLoading(false);
                setHasResolvedInitialResults(true);
            }, 500);

            return () => {
                clearTimeout(timer);
            };
        } else if (searchQuery.length > 0 && searchQuery.length < 3) {
            // Show message for short search queries
            setDataToShow([]);
            setIsLoading(false);
            setHasResolvedInitialResults(true);
        } else if (geoLoading) {
            // Keep the real card skeleton in place until geolocation either
            // returns nearby courts or fails and we can show curated parks.
            // This avoids flashing the empty "start typing" state in between.
            setDataToShow([]);
            setIsLoading(true);
            setHasResolvedInitialResults(false);
        } else if (data !== null && data.length > 0 && favoritesLoaded) {
            // Show popular parks by default when no search query and geolocation is not available
            // This fixes the issue where parks don't load when saved to homepage
            setIsLoading(true);
            const timer = setTimeout(() => {
                // Filter data to only include popular parks
                const popularParks = data.filter(item => POPULAR_PARKS_DEFAULT.includes(item.id.toString()));

                // First, split popular courts into available and unavailable
                const { available, unavailable } = getFilteredCourts(popularParks);

                // Sort by the order in POPULAR_PARKS_DEFAULT array to maintain curated order
                const sortByPopularityOrder = (a, b) => {
                    const indexA = POPULAR_PARKS_DEFAULT.indexOf(a.id.toString());
                    const indexB = POPULAR_PARKS_DEFAULT.indexOf(b.id.toString());
                    return indexA - indexB;
                };

                const sortedAvailable = available.sort(sortByPopularityOrder);
                const sortedUnavailable = unavailable.sort(sortByPopularityOrder);

                // Combine them with available courts first
                const combinedSorted = [...sortedAvailable, ...sortedUnavailable];

                setDataToShow(combinedSorted.filter(item => !favorites.includes(parseInt(item.id))));
                setIsLoading(false);
                setHasResolvedInitialResults(true);
            }, 500);

            return () => {
                clearTimeout(timer);
            };
        } else {
            // Data has not arrived yet, so the initial skeleton remains.
            setDataToShow([]);
            setIsLoading(true);
            setHasResolvedInitialResults(false);
        }
    }, [data, searchQuery, favoritesLoaded, geoLoading]);

    const hasAvailabilities = (court) => {
        return court.tableStats && court.tableStats.some(stat =>
            Object.values(stat.availability).some(slots => slots && slots.length > 0)
        );
    };

    // Split courts into those with and without availabilities
    const getFilteredCourts = (courts) => {
        const available = [];
        const unavailable = [];

        courts.forEach(court => {
            if (hasAvailabilities(court)) {
                available.push(court);
            } else {
                unavailable.push(court);
            }
        });

        return { available, unavailable };
    };

    return (
        <div className="availability-view" style={{ verticalAlign: 'top', minHeight: '84vh' }}>
            <Typography
                variant="h4"
                component="h1"
            >
                {isFrench ? "Trouvez un terrain" : "Find a court"}
            </Typography>
            <Typography className="availability-subtitle">
                {isFrench ? "Terrains municipaux de Montréal · disponibilités en direct" : "Montréal municipal courts · live availability"}
            </Typography>
            <Input
                className="court-search-input"
                type="text"
                style={{
                    height: isMobile ? '40px' : '50px',
                    backgroundColor: 'var(--cc-surface)',
                    marginBottom: '1em',
                }}
                value={searchQuery}
                onChange={(e) => {
                    setSearchQuery(e.target.value)
                    logEvent(analytics, 'search_change', { search_term: e.target.value });
                }}
                placeholder={isFrench ? "Ex: La Fontaine" : "Ex: La Fontaine"}
            />
            {/* Favorites Section - Always visible if loaded */}
            {favoritesLoaded && favorites.length > 0 && (
                <>
                    <Typography className="favorites-heading" variant="h4" component="h2" style={{ fontSize: isMobile ? '24px' : '36px', color: 'var(--cc-text)' }}>
                        {isFrench ? "Mes favoris" : "My Favorites"}
                    </Typography>
                    {favorites.map(favId => {
                        const favCourt = data.find(court => court.id == favId);
                        return favCourt && <CourtAvailabilityTable
                            style={{ marginTop: "20px" }}
                            data={favCourt}
                            key={favCourt.id}
                            isFrench={isFrench}
                            analytics={analytics}
                            setFavorites={updateFavorites}
                            prevFavorites={favorites}
                            setSelectedTab={setSelectedTab}
                            isExpanded={expandedCourts.has(favCourt.id)}
                            onExpansionChange={(isExpanded) => handleCourtExpansion(favCourt.id, isExpanded)}
                            selectedDate={selectedDates[favCourt.id]}
                            onDateChange={handleTabChange}
                            accountType={effectiveAccountType}
                        />
                    })}
                    <hr style={{ borderColor: 'var(--cc-border)', margin: '1.25em 0' }} />
                </>
            )}

            {!geoLoading && geoPermission !== 'granted' && data.length > 0 && (
                <div className="location-distance-prompt" aria-label={isFrench ? "Distance des terrains" : "Court distances"}>
                    <Typography variant="body2">
                        {geoPermission === 'denied'
                            ? (isFrench
                                ? "La localisation est désactivée. Autorisez-la pour CourtClic, puis réessayez pour voir les distances."
                                : "Location is blocked. Allow it for CourtClic, then try again to see distances.")
                            : (isFrench
                                ? "Utilisez votre position pour afficher la distance de chaque terrain."
                                : "Use your location to show the distance to every court.")}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={requestCourtDistances}
                    >
                        {isFrench ? "Utiliser ma position" : "Use my location"}
                    </Button>
                </div>
            )}

            {/* Geolocation Loading Indicator - Visible when showing popular parks but refining location */}
            {geoLoading && !isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5em' }}>
                    <style>{`
                        @keyframes pulse-red {
                            0% { transform: scale(0.95); box-shadow: 0 0 0 0 var(--cc-focus); }
                            70% { transform: scale(1); box-shadow: 0 0 0 10px transparent; }
                            100% { transform: scale(0.95); box-shadow: 0 0 0 0 transparent; }
                        }
                    `}</style>
                    <div style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: 'var(--cc-accent)',
                        borderRadius: '50%',
                        marginRight: '10px',
                        animation: 'pulse-red 2s infinite'
                    }}></div>
                    <Typography variant="body2" style={{ color: 'var(--cc-text-soft)', fontStyle: 'italic' }}>
                        {isFrench ? "Recherche des terrains à proximité..." : "Finding courts near you..."}
                    </Typography>
                </div>
            )}

            {!hasLoadedInitialData || !hasResolvedInitialResults ? (
                <CourtCardsSkeleton
                    isFrench={isFrench}
                    hasError={loadFailed || isOffline}
                    isOffline={isOffline}
                    onRetry={() => setLoadAttempt(current => current + 1)}
                />
            ) : (
                <>
                    {/* Results Section */}
                    {dataToShow.length === 0 ? (
                        searchQuery.length >= 3 ? (
                            <Typography variant="body1" style={{ textAlign: 'center', marginTop: '2em', color: 'var(--cc-text-soft)' }}>
                                {isFrench ? "Aucun terrain trouvé pour votre recherche." : "No courts found for your search."}
                            </Typography>
                        ) : searchQuery.length > 0 ? (
                            <Typography variant="body1" style={{ textAlign: 'center', marginTop: '2em', color: 'var(--cc-text-soft)' }}>
                                {isFrench ? "Veuillez entrer au moins 3 caractères." : "Please enter at least 3 characters."}
                            </Typography>
                        ) : (
                            <div style={{ textAlign: 'center', marginTop: '2em' }}>
                                {geoPermission === 'unknown' && !geoLoading && (
                                    <CircularProgress size={20} style={{ color: 'var(--cc-accent)', marginBottom: '0.5em' }} />
                                )}
                                <Typography variant="body1" style={{ color: 'var(--cc-text-soft)' }}>
                                    {geoPermission === 'denied' ?
                                        (isFrench ? "Autorisez la géolocalisation ou commencez à taper pour voir les terrains." : "Allow location access or start typing to see courts.") :
                                        geoPermission === 'unknown' ?
                                            (isFrench ? "Recherche des terrains à proximité..." : "Looking for courts near you...") :
                                            (isFrench ? "Commencez à taper pour voir les résultats de recherche." : "Start typing to see search results.")
                                    }
                                </Typography>
                            </div>
                        )
                    ) : (
                        <>
                            {!geoLoading && (
                                <>
                                    {!favoritesLoaded && (
                                        <Typography variant="h4" component="h2" style={{ fontSize: isMobile ? '24px' : '36px', marginTop: "1em", marginBottom: "1.5em", color: 'var(--cc-text)' }}>
                                            {isFrench ? "Terrains populaires" : "Popular Courts"}
                                        </Typography>
                                    )}

                                    {/* If we are showing dataToShow but NOT favorites logic (which we handle separately above),
                                    we should probably have a header if it's proximity results?
                                    The original code didn't have a header for the main list, but let's add one for clarity/smoothness if needed.
                                    For now, let's keep it simple to avoid doubling headers.
                                    */}

                                    {getFilteredCourts(dataToShow).available.map((item) => (
                                        <CourtAvailabilityTable
                                            style={{ marginTop: "20px" }}
                                            data={item}
                                            key={item.id}
                                            isFrench={isFrench}
                                            analytics={analytics}
                                            setFavorites={updateFavorites}
                                            prevFavorites={favorites}
                                            setSelectedTab={setSelectedTab}
                                            isExpanded={expandedCourts.has(item.id)}
                                            onExpansionChange={(isExpanded) => handleCourtExpansion(item.id, isExpanded)}
                                            selectedDate={selectedDates[item.id]}
                                            onDateChange={handleTabChange}
                                            accountType={effectiveAccountType}
                                        />
                                    ))}

                                    {/* Unavailable courts section */}
                                    {getFilteredCourts(dataToShow).unavailable.length > 0 && (
                                        <>
                                            <div
                                                onClick={() => setShowNoAvailability(!showNoAvailability)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginTop: '1.5em',
                                                    marginBottom: '1em',
                                                    opacity: 0.8,
                                                    cursor: 'pointer',
                                                    padding: '10px 0'
                                                }}
                                            >
                                                <Typography variant="h6" style={{ margin: '0 15px', color: 'var(--cc-text-soft)', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '1px', display: 'flex', alignItems: 'center' }}>
                                                    {isFrench ? `Terrains sans disponibilité (${getFilteredCourts(dataToShow).unavailable.length})` : `Courts with no availability (${getFilteredCourts(dataToShow).unavailable.length})`}
                                                    <span style={{ marginLeft: '10px', fontSize: '0.8rem' }}>{showNoAvailability ? '▲' : '▼'}</span>
                                                </Typography>
                                            </div>

                                            {showNoAvailability && getFilteredCourts(dataToShow).unavailable.map((item) => (
                                                <CourtAvailabilityTable
                                                    style={{ marginTop: "20px", opacity: 0.8 }}
                                                    data={item}
                                                    key={item.id}
                                                    isFrench={isFrench}
                                                    analytics={analytics}
                                                    setFavorites={updateFavorites}
                                                    prevFavorites={favorites}
                                                    setSelectedTab={setSelectedTab}
                                                    isExpanded={expandedCourts.has(item.id)}
                                                    onExpansionChange={(isExpanded) => handleCourtExpansion(item.id, isExpanded)}
                                                    selectedDate={selectedDates[item.id]}
                                                    onDateChange={handleTabChange}
                                                    accountType={effectiveAccountType}
                                                />
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            <FavoriteLimitDialog />
        </div>
    );
};

export default CourtAvailabilityView;
